"use client";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { iconsMap } from "@/components/icons";
import { Reveal } from "@/components/reveal";
import { PortableText } from "next-sanity";
import { getURL } from "@/lib/utils";
import { ExtractBySectionType, SectionType } from "types";
import { SanityImage } from "@/components/sanity-image";

export type ValuePropositionType = ExtractBySectionType<SectionType, "valueProposition", "_type">;

interface CommunitySectionProps {
  index?: number;
  data: ValuePropositionType;
}

export function CommunityValueProp({ data, index = 0 }: CommunitySectionProps) {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const isTablet = useMediaQuery("(max-width: 1024px)");
  const isEven = index % 2 === 0;
  const { title, subtitle, description, image, ctas } = data;

  return (
    <section className="py-12 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6 md:px-8 bg-[linear-gradient(rgba(255,255,255,0.94),rgba(255,255,255,0.94)),url('/index/community/noise.svg')] bg-white">
      <div className="max-w-screen-2xl mx-auto">
        <div
          className={`
          grid grid-cols-1 lg:grid-cols-2 
          gap-6 sm:gap-8 md:gap-10 lg:gap-12 xl:gap-16
          items-center
          ${isEven ? "lg:grid-flow-row" : "lg:grid-flow-row-dense"}
        `}
        >
          {/* Image Container */}
          <div
            className={`
            w-full 
            max-w-sm mx-auto sm:max-w-md md:max-w-lg lg:max-w-none
            ${isEven ? "lg:order-2" : "lg:order-1"}
          `}
          >
            {image && (
              <Reveal>
                <AspectRatio ratio={isMobile ? 4 / 3 : isTablet ? 5 / 3.5 : 350 / 250}>
                  <SanityImage
                    src={image}
                    placeholder="blur"
                    quality={60}
                    blurDataURL={image.asset?.metadata?.lqip || ""}
                    alt="event"
                    fill
                    className="object-cover rounded-xl sm:rounded-2xl cursor-pointer"
                    sizes="(max-width: 640px) 90vw, (max-width: 768px) 80vw, (max-width: 1024px) 60vw, (max-width: 1280px) 45vw, 40vw"
                  />
                </AspectRatio>
              </Reveal>
            )}
          </div>

          {/* Content Container */}
          <Reveal
            className={`
              w-full flex flex-col gap-3 sm:gap-4
              text-center lg:text-left
              ${isEven ? "lg:order-1" : "lg:order-2"}
            `}
          >
            {/* Title */}
            <h3
              className="
              text-primary-500 font-medium 
              text-lg sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl
              leading-tight
            "
            >
              {title}
            </h3>

            {/* Subtitle */}
            <h4
              className="
              font-medium 
              text-lg sm:text-xl md:text-2xl lg:text-2xl xl:text-3xl
              leading-tight
            "
            >
              {subtitle}
            </h4>

            {/* Description */}
            {description && (
              <div className="mt-2">
                <PortableText
                  value={description}
                  components={{
                    block: {
                      normal: ({ children }) => (
                        <p
                          className="
                          font-normal text-primary-text-400 leading-relaxed
                          text-sm sm:text-base md:text-lg lg:text-base xl:text-lg
                          max-w-prose mx-auto lg:mx-0
                        "
                        >
                          {children}
                        </p>
                      ),
                    },
                  }}
                />
              </div>
            )}

            {/* CTA Buttons */}
            <div
              className="
              pt-4 sm:pt-6 flex flex-col sm:flex-row gap-3 sm:gap-4 
              items-center justify-center lg:items-start lg:justify-start
            "
            >
              {ctas &&
                ctas.map((cta, ctaIndex) => {
                  const href = getURL(cta);
                  return (
                    <Button
                      variant={cta.variant}
                      radius="full"
                      className="
                        w-full sm:w-fit min-w-[140px]
                        text-sm sm:text-base
                      "
                      size={isMobile ? "default" : isTablet ? "lg" : "xl"}
                      asChild={true}
                      key={ctaIndex}
                    >
                      <Link
                        href={href}
                        target={cta.openInNewTab ? "_blank" : "_self"}
                        rel={cta.openInNewTab ? "noopener noreferrer" : undefined}
                      >
                        <span className="flex items-center gap-2">
                          {cta.textBeforeIcon}
                          {cta.icon && iconsMap[cta.icon]}
                          {cta.textAfterIcon}
                        </span>
                      </Link>
                    </Button>
                  );
                })}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
